import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowUp, Linkedin, Github } from 'lucide-react';

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'about', href: '#about' },
    { key: 'services', href: '#services' },
    { key: 'projects', href: '#projects' },
    { key: 'contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-white/50 border-t border-zinc-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mb-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <a
              href="#home"
              onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
              className="inline-flex items-center gap-2.5 mb-5"
            >
              <div className="w-8 h-8 rounded-lg bg-zinc-950 flex items-center justify-center">
                <span className="font-outfit font-bold text-white text-sm">M</span>
              </div>
              <span className="font-outfit font-semibold text-base text-zinc-950">
                Med Stud <span className="text-sky-500">Tech</span>
              </span>
            </a>
            <p className="font-manrope text-sm text-zinc-500 max-w-xs leading-relaxed mb-5">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3">
              {[
                { icon: Linkedin, href: '#', label: 'LinkedIn' },
                { icon: Github, href: '#', label: 'GitHub' },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 rounded-md bg-white border border-zinc-200 flex items-center justify-center text-zinc-500 hover:text-zinc-950 hover:bg-zinc-50 transition-colors"
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-outfit font-semibold text-xs uppercase tracking-wider mb-5 text-zinc-400">Navigation</h4>
            <ul className="space-y-2.5">
              {navItems.map((item) => (
                <li key={item.key}>
                  <a
                    href={item.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(item.href); }}
                    className="font-manrope text-sm text-zinc-500 hover:text-zinc-950 transition-colors"
                  >
                    {t(`nav.${item.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-outfit font-semibold text-xs uppercase tracking-wider mb-5 text-zinc-400">Légal</h4>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="/mentions-legales"
                  className="font-manrope text-sm text-zinc-500 hover:text-zinc-950 transition-colors"
                >
                  {t('footer.links.legal')}
                </a>
              </li>
              <li className="font-manrope text-xs text-zinc-400 font-mono">
                SIREN: 924 618 275
              </li>
              <li className="font-manrope text-xs text-zinc-400 font-mono">
                SIRET: 924 618 275 00011
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-zinc-200">
          <p className="font-manrope text-xs text-zinc-400 mb-4 md:mb-0">
            © {currentYear} Med Stud Tech. {t('footer.rights')}
          </p>

          <motion.button
            onClick={scrollToTop}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-zinc-200 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-50 transition-colors text-xs"
            whileTap={{ scale: 0.98 }}
            data-testid="scroll-to-top"
          >
            <ArrowUp className="w-3 h-3" />
            <span>Retour en haut</span>
          </motion.button>
        </div>
      </div>
    </footer>
  );
}
